import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useVisibleSections } from './useVisibleSections';
import {
  createContext,
  useContextSelector,
  useContext,
} from 'use-context-selector';

type ContextValue = ReturnType<typeof useVisibleSections>;

export const VisibleSectionsContext = createContext<ContextValue | undefined>(
  undefined
);

interface Props {
  children?: ReactNode;
}

/**
 * Handles company report visible sections
 */
export function VisibleSectionsProvider({ children }: Props) {
  const {
    visibleSections,
    nextSectionLabel,
    previousSectionLabel,
    loadNextSection,
    loadPreviousSection,
    loadEverySection,
    hasNextSection,
    hasPreviousSection,
    loadSection,
    handleSectionOnMount,
    scrollToSection,
    isPristine,
    getSectionLabel,
  } = useVisibleSections();

  const value = useMemo(
    () => ({
      visibleSections,
      nextSectionLabel,
      previousSectionLabel,
      loadNextSection,
      loadPreviousSection,
      loadEverySection,
      hasNextSection,
      hasPreviousSection,
      loadSection,
      handleSectionOnMount,
      scrollToSection,
      isPristine,
      getSectionLabel,
    }),
    [
      visibleSections,
      nextSectionLabel,
      previousSectionLabel,
      loadNextSection,
      loadPreviousSection,
      loadEverySection,
      hasNextSection,
      hasPreviousSection,
      loadSection,
      handleSectionOnMount,
      scrollToSection,
      isPristine,
      getSectionLabel,
    ]
  );
  return (
    <VisibleSectionsContext.Provider value={value}>
      {children}
    </VisibleSectionsContext.Provider>
  );
}

export function useVisibleSectionsContext() {
  const context = useContext(VisibleSectionsContext);

  if (context) {
    return context;
  }

  throw new Error(
    'Attempted to access VisibleSectionsContext outside of a VisibleSectionsProvider'
  );
}

/**
 * This hook contains a slice of the above context to prevent unnecessary re-renders.
 * isPristine field signifies whether there is only one visible section loaded
 * which is important for SEO purposes as we adjust headline levels based on that
 * to support ranking of individual sections independently.
 * Pristine sections also skip virtualization as they receive SSRed HTML which
 * only needs to be hydrated and we want to prevent unmounting those sections.
 * The reason behind utilising useContextSelector is that
 * useVisibleSectionsContext changes every time we move to another section and that causes
 * any component that consumes this context re-render.
 * However by consuming useVisibleSectionsIsPristine we can eliminate that as
 * this flag only changes once when a second section is loaded.
 */
export const useVisibleSectionsIsPristine = () => {
  return useContextSelector(
    VisibleSectionsContext,
    (value) => value?.isPristine ?? true
  );
};
